/**
 * Customer Events Tracker
 * 
 * Centralized tracking for form submissions, conversions, and custom pixels.
 * This file handles all customer event tracking in a single location.
 * 
 * PATTERN: Data Attribute → Event Detection → Pixel Tracking → Optional Redirect
 * 
 * Usage in Liquid:
 *   {% if form.posted_successfully? %}
 *     <div data-form-success="true" data-form-type="contact" data-redirect-url="/pages/thank-you"></div>
 *   {% endif %}
 * 
 * The pattern automatically:
 * 1. Detects form success via data attributes
 * 2. Sends custom pixel events with form context
 * 3. Redirects to thank you page (if data-redirect-url provided)
 */

class CustomerEventsTracker {
  /**
   * Form Event Registry
   * 
   * Define all trackable forms here with their pixel event names
   * and any additional tracking metadata.
   */
  static FORM_EVENTS = {
    contact: {
      name: 'Contact Form Submitted',
      eventId: 'form_contact_submitted',
      description: 'Fired when contact form is successfully submitted',
      pixelEvent: 'Contact',
      metadata: {
        category: 'Lead Generation',
        value: 0
      }
    },
    
    quote: {
      name: 'Quote Form Submitted',
      eventId: 'form_quote_submitted',
      description: 'Fired when rental quote form is successfully submitted',
      pixelEvent: 'InitiateCheckout',
      metadata: {
        category: 'Quote Request',
        value: 0
      }
    },
    
    newsletter: {
      name: 'Newsletter Signup',
      eventId: 'form_newsletter_submitted',
      description: 'Fired when newsletter signup form is successfully submitted',
      pixelEvent: 'Subscribe',
      metadata: {
        category: 'Newsletter',
        value: 0
      }
    },
    
    support: {
      name: 'Support Form Submitted',
      eventId: 'form_support_submitted',
      description: 'Fired when support form is successfully submitted',
      pixelEvent: 'Contact',
      metadata: {
        category: 'Customer Support',
        value: 0
      }
    },
    
    question: {
      name: 'Product Question Submitted',
      eventId: 'form_question_submitted',
      description: 'Fired when product question form is successfully submitted',
      pixelEvent: 'Contact',
      metadata: {
        category: 'Product Inquiry',
        value: 0
      }
    },
    
    catalog: {
      name: 'Catalog Request Submitted',
      eventId: 'form_catalog_submitted',
      description: 'Fired when catalog request form is successfully submitted',
      pixelEvent: 'Contact',
      metadata: {
        category: 'Catalog Request',
        value: 0
      }
    }
  };
  
  constructor() {
    this.debugMode = false;
    this.eventLog = [];
    
    this.init();
  }
  
  /**
   * Initialize tracker - setup form tracking on page load
   */
  init() {
    document.addEventListener('DOMContentLoaded', () => {
      this.setupFormTracking();
      this.checkForSuccessRedirect();
    });
    
    if (this.debugMode) {
      console.log('✅ CustomerEventsTracker initialized');
    }
  }
  
  /**
   * Check for successful form submission and redirect if needed
   * 
   * After a form is successfully submitted, Shopify reloads the page.
   * We detect success via data attributes and redirect to thank you page.
   * 
   * Usage in Liquid:
   *   {% if form.posted_successfully? %}
   *     <div data-form-success="true" data-form-type="contact" data-redirect-url="/pages/thank-you"></div>
   *   {% endif %}
   */
  checkForSuccessRedirect() {
    const successElement = document.querySelector('[data-form-success="true"]');
    
    if (!successElement) {
      if (this.debugMode) {
        console.log('📋 No form success detected');
      }
      return;
    }
    
    const formType = successElement.getAttribute('data-form-type');
    const redirectUrl = successElement.getAttribute('data-redirect-url');
    
    if (!formType) {
      console.error('❌ Form success detected but data-form-type missing');
      return;
    }
    
    if (this.debugMode) {
      console.log(`✅ Form successfully submitted: ${formType}`);
    }
    
    // Track the successful submission
    this.trackFormSubmission(formType, {
      success: true,
      redirecting: !!redirectUrl
    });
    
    // NOTE: Redirect is handled by inline script in Liquid template
    // to prevent scroll issues. This method only tracks the event.
  }
  
  /**
   * Setup form tracking to fire events immediately on submit
   * 
   * Finds all forms with data-track-submission attribute and sends
   * tracking events immediately when submitted (before server validation).
   * 
   * Usage in Liquid:
   *   <form data-track-submission="contact">
   *   <form data-track-submission="quote">
   */
  setupFormTracking() {
    const forms = document.querySelectorAll('form[data-track-submission]');
    
    if (forms.length === 0) {
      if (this.debugMode) {
        console.log('📋 No forms with data-track-submission found');
      }
      return;
    }
    
    forms.forEach(form => {
      const formType = form.getAttribute('data-track-submission');
      
      form.addEventListener('submit', (e) => {
        // Don't prevent submission, just track it
        const formData = new FormData(form);
        const capturedData = this.extractFormData(formData, formType);
        
        // Add metadata
        capturedData._formType = formType;
        capturedData._timestamp = new Date().toISOString();
        capturedData._url = window.location.href;
        
        // Track immediately (before form submits to server)
        this.trackFormSubmission(formType, capturedData);
        
        if (this.debugMode) {
          console.log(`📋 Form submitted and tracked (${formType}):`, capturedData);
        }
      });
      
      if (this.debugMode) {
        console.log(`📋 Form tracking setup: ${formType}`);
      }
    });
  }
  
  /**
   * Extract all form data dynamically
   * 
   * Converts FormData to a clean object, handling different field name patterns
   * (e.g., contact[name], customer[email], simple field names, etc.)
   * 
   * @param {FormData} formData - FormData object from form
   * @param {string} formType - Type of form (contact, quote, etc.)
   * @returns {object} Clean object with all form fields
   */
  extractFormData(formData, formType) {
    const data = {};
    
    // Iterate through all form fields
    for (const [key, value] of formData.entries()) {
      // Skip empty values
      if (!value || value === '') continue;
      
      // Skip honeypot fields (common spam prevention)
      if (key.includes('honeypot') || key.includes('bot')) continue;
      
      // Extract clean field name
      // Examples:
      //   "contact[name]" → "name"
      //   "customer[email]" → "email"
      //   "rental_dates" → "rental_dates"
      //   "form_type" → "form_type"
      const cleanKey = this.cleanFieldName(key);
      
      data[cleanKey] = value;
    }
    
    return data;
  }
  
  /**
   * Clean field names from form namespacing
   * 
   * @param {string} fieldName - Original field name
   * @returns {string} Cleaned field name
   */
  cleanFieldName(fieldName) {
    // Remove namespace prefixes: "contact[name]" → "name"
    const bracketMatch = fieldName.match(/\[([^\]]+)\]/);
    if (bracketMatch) {
      return bracketMatch[1];
    }
    
    // Return as-is if no brackets
    return fieldName;
  }
  
  
  /**
   * Track form submission
   * 
   * @param {string} formType - Type of form from FORM_EVENTS
   * @param {object} customData - Additional tracking data
   */
  trackFormSubmission(formType, customData = {}) {
    const event = CustomerEventsTracker.FORM_EVENTS[formType];
    
    if (!event) {
      console.error(`❌ CustomerEventsTracker: Unknown form type "${formType}". Check FORM_EVENTS registry.`);
      console.error('Available form types:', Object.keys(CustomerEventsTracker.FORM_EVENTS));
      return;
    }
    
    // Build event payload
    const payload = {
      eventId: event.eventId,
      eventName: event.name,
      formType: formType,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      ...event.metadata,
      ...customData
    };
    
    // Send to pixels
    this.sendToPixels(event.pixelEvent, payload);
    
    // Log event
    this.logEvent(payload);
    
    if (this.debugMode) {
      console.log(`📊 CustomerEventsTracker: ${event.name}`, payload);
    }
  }
  
  /**
   * Send event to Shopify Customer Events API and Custom Pixels
   * 
   * Uses DOM data attributes for sandbox-safe communication with Custom Pixels.
   * 
   * @param {string} pixelEvent - Event name for pixels (e.g., 'Contact', 'InitiateCheckout')
   * @param {object} payload - Event data
   */
  sendToPixels(pixelEvent, payload) {
    // 1. Send to legacy Shopify Customer Events API (for built-in integrations)
    if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
      window.Shopify.analytics.publish('raeco:custom_event', {
        name: payload.eventId,
        data: payload
      });
      
      if (this.debugMode) {
        console.log(`📊 Shopify Customer Events (Legacy): ${payload.eventId}`, payload);
      }
    }
    
    // 2. Send to Custom Pixels via data attribute (sandbox-safe, no warnings)
    // Create a hidden element with form data that the pixel can read
    const dataElement = document.createElement('div');
    dataElement.setAttribute('data-raeco-form-submission', JSON.stringify({
      eventName: payload.eventId,
      eventData: payload
    }));
    dataElement.style.display = 'none';
    document.body.appendChild(dataElement);
    
    if (this.debugMode) {
      console.log(`📊 Data attribute added for Custom Pixel:`, payload);
    }
  }
  
  /**
   * Log event to internal history
   * 
   * @param {object} payload - Event data
   */
  logEvent(payload) {
    this.eventLog.push(payload);
    
    // Keep only last 50 events
    if (this.eventLog.length > 50) {
      this.eventLog.shift();
    }
  }
  
  /**
   * Get event log
   * 
   * @returns {array} Array of logged events
   */
  getEventLog() {
    return [...this.eventLog];
  }
  
  /**
   * Print statistics
   */
  printStats() {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📊 Customer Events Tracker Statistics');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    
    console.log('\n📋 Registered Form Events:');
    Object.entries(CustomerEventsTracker.FORM_EVENTS).forEach(([key, event]) => {
      console.log(`  ${key}: ${event.name} (${event.pixelEvent})`);
    });
    
    if (this.eventLog.length > 0) {
      console.log(`\n📝 Events Logged: ${this.eventLog.length}`);
      console.log('Recent events:');
      this.eventLog.slice(-5).forEach(entry => {
        console.log(`  ${entry.timestamp}: ${entry.eventName}`);
      });
    }
    
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
  }
}

// Create global singleton instance
if (!window.customerEventsTracker) {
  window.customerEventsTracker = new CustomerEventsTracker();
  
  console.log('✅ CustomerEventsTracker initialized', {
    debugMode: 'window.customerEventsTracker.debugMode = true',
    stats: 'window.customerEventsTracker.printStats()',
    log: 'window.customerEventsTracker.getEventLog()'
  });
}

// Export for use in modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CustomerEventsTracker;
}
